import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import {
  Users,
  Briefcase,
  ClipboardList,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Target,
  Zap,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMDashboard() {
  const { user, token, isAdmin } = useCRMAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLogs, setRecentLogs] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await fetch(`${API}/api/crm/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color, link }) => (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {link && (
        <Link to={link} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 mt-4">
          View details <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </Card>
  );

  const QuickActionCard = ({ title, description, icon: Icon, color, link }) => (
    <Link to={link}>
      <Card className="p-4 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer group">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color} group-hover:scale-110 transition-transform`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{title}</p>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
        </div>
      </Card>
    </Link>
  );

  return (
    <CRMLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}! 👋
        </h1>
        <p className="text-slate-500 mt-1">Here's what's happening in your agency today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Clients"
          value={stats?.total_clients || 0}
          icon={Briefcase}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          trend="up"
          trendValue="+12% this month"
          link="/crm/clients"
        />
        <StatCard
          title="Team Members"
          value={(stats?.total_employees || 0) + (stats?.total_freelancers || 0)}
          icon={Users}
          color="bg-gradient-to-br from-emerald-500 to-emerald-600"
          link="/crm/team"
        />
        <StatCard
          title="Pending Tasks"
          value={stats?.pending_tasks || 0}
          icon={ClipboardList}
          color="bg-gradient-to-br from-amber-500 to-orange-500"
          link="/crm/tasks"
        />
        <StatCard
          title="Today's Work"
          value={stats?.today_work_logs || 0}
          icon={Clock}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          link="/crm/work-logs"
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionCard
            title="Add Client"
            description="Register new client"
            icon={Briefcase}
            color="bg-gradient-to-br from-blue-500 to-indigo-600"
            link="/crm/clients/new"
          />
          <QuickActionCard
            title="Create Task"
            description="Assign work to team"
            icon={ClipboardList}
            color="bg-gradient-to-br from-emerald-500 to-teal-600"
            link="/crm/tasks/new"
          />
          <QuickActionCard
            title="Log Work"
            description="Record completed work"
            icon={Clock}
            color="bg-gradient-to-br from-purple-500 to-pink-600"
            link="/crm/work-logs/new"
          />
          <QuickActionCard
            title="View Reports"
            description="Client & team reports"
            icon={BarChart3}
            color="bg-gradient-to-br from-amber-500 to-orange-600"
            link="/crm/reports"
          />
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Pending Tasks</h3>
            <Link to="/crm/tasks" className="text-sm text-indigo-600 hover:text-indigo-700">View all</Link>
          </div>
          
          {pendingTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-500">All caught up! No pending tasks.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map(task => (
                <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-orange-500' :
                    task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{task.title}</p>
                    <p className="text-sm text-slate-500">{task.client_name}</p>
                  </div>
                  <Badge variant="outline">{task.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Activity */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Work Logs</h3>
            <Link to="/crm/work-logs" className="text-sm text-indigo-600 hover:text-indigo-700">View all</Link>
          </div>
          
          {recentLogs.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No work logs yet today.</p>
              <Button asChild className="mt-3">
                <Link to="/crm/work-logs/new">Log Your First Work</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentLogs.map(log => (
                <div key={log.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{log.work_type_name}</p>
                    <p className="text-sm text-slate-500">{log.client_name} • {log.quantity} {log.unit}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Production Overview (Admin Only) */}
      {isAdmin() && (
        <Card className="p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-slate-900">This Month's Production</h3>
              <p className="text-sm text-slate-500">Overview of agency output</p>
            </div>
            <Link to="/crm/kpi">
              <Button variant="outline">
                <Target className="w-4 h-4 mr-2" /> View KPIs
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.month_production || 0}</p>
              <p className="text-sm text-blue-600/70">Total Deliverables</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-emerald-600">₹{(stats?.month_revenue || 0).toLocaleString()}</p>
              <p className="text-sm text-emerald-600/70">Production Value</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-purple-600">{stats?.completed_tasks || 0}</p>
              <p className="text-sm text-purple-600/70">Tasks Completed</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl text-center">
              <p className="text-3xl font-bold text-amber-600">{stats?.total_clients || 0}</p>
              <p className="text-sm text-amber-600/70">Active Clients</p>
            </div>
          </div>
        </Card>
      )}
    </CRMLayout>
  );
}
